import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { 
  Globe, LogOut, LayoutGrid, Mail, Eye, Settings,
  Save, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import SiteEditor from '@/components/SiteEditor';

// Client Dashboard - for clients to manage their own site
export default function ClientDashboard({ user, onLogout }) {
  const [site, setSite] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      // Fetch the client's site
      const sitesRes = await api.get('/sites');
      const sites = sitesRes.data.sites || [];
      
      if (sites.length > 0) {
        setSite(sites[0]);
        
        // Fetch enquiries for this site
        try {
          const enquiriesRes = await api.get(`/sites/${sites[0].id}/enquiries`);
          setEnquiries(enquiriesRes.data.enquiries || []);
        } catch (e) {
          // Enquiries might not exist yet
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePublish = async () => {
    if (!site) return;
    try {
      await api.post(`/sites/${site.id}/publish`);
      setSite(prev => ({...prev, status: 'published'}));
      toast.success('Site published!');
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const markEnquiryRead = async (enquiryId) => {
    try {
      await api.put(`/enquiries/${enquiryId}/read`);
      setEnquiries(prev => prev.map(e => e.id === enquiryId ? {...e, read: true} : e));
    } catch (error) {
      // Silent fail
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // If in editing mode, show the site editor
  if (isEditing && site) {
    return <SiteEditor site={site} onClose={() => { setIsEditing(false); fetchData(); }} />;
  }

  // No site assigned
  if (!site) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-6 h-6 text-orange-500" />
              <span className="font-semibold">Boxit Dashboard</span>
            </div>
            <Button variant="ghost" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-12">
          <Card className="text-center py-12">
            <CardContent>
              <Globe className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Site Assigned</h2>
              <p className="text-slate-500">
                Your account doesn't have a site assigned yet. Please contact the administrator.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const unreadCount = enquiries.filter(e => !e.read).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">{site.performer_name}</h1>
              <p className="text-xs text-slate-500">{site.slug}.boxit.site</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Site Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Site Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Status</span>
                <Badge className={site.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                  {site.status === 'published' ? 'Live' : 'Draft'}
                </Badge>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Package</span>
                <Badge variant="outline" className="capitalize">{site.package}</Badge>
              </div>
              {site.status === 'published' && (
                <a 
                  href={`/sites/${site.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-orange-500 hover:text-orange-600 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  View live site
                </a>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start" variant="outline" onClick={() => setIsEditing(true)}>
                <LayoutGrid className="w-4 h-4 mr-2" />
                Edit Site Content
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => window.open(`/sites/${site.slug}`, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Site
              </Button>
              {site.status !== 'published' && (
                <Button className="w-full justify-start bg-emerald-500 hover:bg-emerald-400" onClick={handlePublish}>
                  <Globe className="w-4 h-4 mr-2" />
                  Publish Site
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Enquiries */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                Enquiries
                {unreadCount > 0 && (
                  <Badge className="bg-orange-500">{unreadCount} new</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {enquiries.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No enquiries yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-auto">
                  {enquiries.slice(0, 5).map(enquiry => (
                    <div 
                      key={enquiry.id}
                      onClick={() => markEnquiryRead(enquiry.id)}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer transition-colors",
                        enquiry.read ? "bg-slate-50" : "bg-orange-50 border border-orange-200"
                      )}
                    >
                      <div className="font-medium text-sm">{enquiry.name}</div>
                      <div className="text-xs text-slate-500 truncate">{enquiry.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Enquiries Table */}
        {enquiries.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>All Enquiries</CardTitle>
              <CardDescription>Messages from your contact form</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-slate-600">Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-slate-600">Email</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-slate-600">Message</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-slate-600">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {enquiries.map(enquiry => (
                      <tr key={enquiry.id} className={!enquiry.read ? 'bg-orange-50' : ''}>
                        <td className="px-4 py-3 font-medium">{enquiry.name}</td>
                        <td className="px-4 py-3 text-slate-600">{enquiry.email}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{enquiry.message}</td>
                        <td className="px-4 py-3 text-slate-500 text-sm">
                          {new Date(enquiry.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
